import type { CatalogProduct } from '../../types'
import ProductAttributesSection from './ProductAttributesSection'
import ProductDocumentsSection from './ProductDocumentsSection'
import type { ProductCustomFieldRow, ProductCustomFieldSection } from './helpers'

type Props = {
  productData: CatalogProduct
  description?: string
  locale?: string
  summaryCustomRows: ProductCustomFieldRow[]
  highlightsCustomRows: ProductCustomFieldRow[]
  sidebarCustomRows: ProductCustomFieldRow[]
  specsSections: ProductCustomFieldSection[]
  detailSections: ProductCustomFieldSection[]
}

const dedupeRows = (rows: ProductCustomFieldRow[]) => {
  const seen = new Set<string>()
  return rows.filter((row) => {
    if (seen.has(row.field.key)) return false
    seen.add(row.field.key)
    return true
  })
}

const isGenericSpecsSection = (section: ProductCustomFieldSection) =>
  /^(prestazioni|specifiche tecniche|dati tecnici)$/i.test(section.title.trim())

const ProductExtendedSections = ({
  productData,
  description,
  locale,
  summaryCustomRows,
  highlightsCustomRows,
  sidebarCustomRows,
  specsSections,
  detailSections,
}: Props) => {
  const resolvedDescription = description ?? productData.description ?? ''
  const hasDescription = Boolean(resolvedDescription.trim())
  const promotedSpecsRows = specsSections.filter(isGenericSpecsSection).flatMap((section) => section.rows)
  const renderedSpecsSections = specsSections.filter((section) => !isGenericSpecsSection(section))
  const overviewRows = dedupeRows([
    ...highlightsCustomRows,
    ...summaryCustomRows,
    ...sidebarCustomRows,
    ...promotedSpecsRows,
  ])
  const hasSpecs = overviewRows.length > 0 || renderedSpecsSections.length > 0 || detailSections.length > 0

  return (
    <div className="space-y-10">
      {hasDescription ? (
        <section id="product-description">
          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Panoramica</p>
            <h2 className="text-2xl font-black tracking-tight text-slate-950">Descrizione</h2>
            <p className="max-w-3xl whitespace-pre-line text-base leading-relaxed text-slate-700">
              {resolvedDescription.trim()}
            </p>
          </div>
        </section>
      ) : null}

      {hasSpecs ? (
        <section
          id="product-specifications"
          aria-labelledby="product-specifications-title"
          className="border-t border-slate-200 pt-7"
        >
          <div className="mb-7">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Specifiche</p>
              <h2 id="product-specifications-title" className="mt-1 text-3xl font-black tracking-tight text-slate-950">
                Dati tecnici
              </h2>
            </div>
          </div>

          <div className="space-y-6">
            {overviewRows.length > 0 ? (
              <ProductAttributesSection
                title="Caratteristiche principali"
                rows={overviewRows}
                productData={productData}
                locale={locale}
              />
            ) : null}

            {renderedSpecsSections.map((section) => (
              <ProductAttributesSection
                key={section.id}
                title={section.title}
                rows={section.rows}
                layoutType={section.layoutType}
                productData={productData}
                locale={locale}
              />
            ))}

            {detailSections.map((section) => (
              <ProductAttributesSection
                key={section.id}
                title={section.title}
                rows={section.rows}
                layoutType={section.layoutType}
                productData={productData}
                locale={locale}
              />
            ))}
          </div>
        </section>
      ) : null}

      <ProductDocumentsSection documents={productData.documents ?? []} />
    </div>
  )
}

export default ProductExtendedSections
